import { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import PlaceOutlinedIcon from "@mui/icons-material/PlaceOutlined";
import YouTubeThumbnail from "@/components/general/youTubeThumbnail";
import AppointmentRequestSampleIcon from "@/icons/appointmentRequestSampleIcon";
import BigRoundButton from "@/components/general/bigRoundButton";
import styles from "@/components/appointmentRequest/appointmentListItem.module.css";

export interface RowAction {
  key: string;
  tooltip: string;
  onClick: () => void;
  icon?: string;
  disabled?: boolean;
  dataTestId?: string;
}

export const formatAppointmentLocation = (item: any): string => {
  const building = item?.building_information;
  if (!building) {
    return "";
  }

  const { name, street_house, zip, city } = building;
  const locationParts = [];

  if (name) locationParts.push(name);
  if (street_house) locationParts.push(street_house);

  if (zip || city) {
    const combined = [zip, city].filter((part) => part != null).join(" ");
    locationParts.push(combined);
  }

  return locationParts.join(", ");
};

export function AppointmentListRow(props: {
  children: ReactNode;
  className?: string;
  dataTestId?: string;
}) {
  return (
    <li className={styles.row} data-testid={props.dataTestId}>
      <span className={styles.iconWrapper} aria-hidden>
        <AppointmentRequestSampleIcon
          width="2.5rem"
          height="2.5rem"
          withBackground={true}
        />
      </span>
      <div className={props.className ?? styles.content}>{props.children}</div>
    </li>
  );
}

export function AppointmentLocationLine(props: { item: any }) {
  const location = formatAppointmentLocation(props.item);
  if (!location) {
    return null;
  }
  return (
    <span className={styles.metaLine}>
      <PlaceOutlinedIcon fontSize="inherit" />
      {location}
    </span>
  );
}

export function CustomerInformationNote(props: { item: any }) {
  const { t } = useTranslation();
  const customerInformation =
    props.item?.building_information?.customer_information;
  if (!customerInformation) {
    return null;
  }
  return (
    <div className={styles.customerInformation}>
      <span className={styles.customerInformationLabel}>
        {t("customer_information")}:
      </span>
      {customerInformation}
    </div>
  );
}

export function ExaminationList(props: { examinations: any[] }) {
  if (!props.examinations?.length) {
    return null;
  }
  return (
    <div className={styles.examinationList}>
      {props.examinations.map((examination: any) => (
        <div key={examination.id + examination.name}>
          <div className={styles.examinationName}>
            {examination.examination_name ?? examination.name}
          </div>
          {examination.description && (
            <div className={styles.examinationDescription}>
              {examination.description}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

export function ExaminationVideoList(props: {
  examinations: any[];
  onPlay: (url: string) => void;
}) {
  const examinationsWithVideo = (props.examinations ?? []).filter(
    (examination: any) => examination.youtube_video_url,
  );
  if (examinationsWithVideo.length === 0) {
    return null;
  }
  return (
    <div className={styles.videoList}>
      {examinationsWithVideo.map((examination: any) => (
        <YouTubeThumbnail
          key={examination.id}
          url={examination.youtube_video_url}
          consentRequired={true}
          onClick={() => props.onPlay(examination.youtube_video_url)}
        />
      ))}
    </div>
  );
}

export function RowActions(props: { actions: RowAction[]; wide: boolean }) {
  if (props.actions.length === 0) {
    return null;
  }
  return (
    <div className={styles.actions}>
      {props.actions.map((action) => (
        <BigRoundButton
          key={action.key}
          dataTestId={action.dataTestId}
          tooltip={action.tooltip}
          onClick={action.onClick}
          icon={action.icon}
          disabled={action.disabled}
          buttonHeight="2.25rem"
          buttonWidth={props.wide ? "2.25rem" : "30vw"}
          borderRadius={props.wide ? undefined : "0.5rem"}
        />
      ))}
    </div>
  );
}
